import React, { useRef } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { MapPin, Star, Download, Share2, CreditCard, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const LocalSitePassPage = () => {
  const { language } = useLanguage();
  const { user } = useOrbitAuth();
  const cardRef = useRef(null);
  const t = (fr, en) => language === 'fr' ? fr : en;

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { year: 'numeric', month: 'long' })
    : (language === 'fr' ? 'Membre' : 'Member');

  const qrData = JSON.stringify({
    type: 'localsite_pass',
    member_id: user?.referral_code || 'MEMBER',
    name: user?.name || 'Membre',
  });

  const rankConfig = {
    mercure: { label: t('Mercure', 'Mercury'), color: '#9CA3AF', gradient: 'from-gray-400 to-gray-500' },
    venus: { label: t('Venus', 'Venus'), color: '#EAB308', gradient: 'from-yellow-400 to-amber-500' },
    terre: { label: t('Terre', 'Earth'), color: '#3B82F6', gradient: 'from-blue-400 to-cyan-500' },
    mars: { label: 'Mars', color: '#EF4444', gradient: 'from-red-400 to-rose-500' },
    jupiter: { label: 'Jupiter', color: '#F97316', gradient: 'from-orange-400 to-amber-500' },
    saturne: { label: t('Saturne', 'Saturn'), color: '#D4943A', gradient: 'from-[#D4943A] to-yellow-500' },
  };

  const rank = rankConfig[user?.rank] || rankConfig.mercure;

  const handleDownload = async () => {
    try {
      const svg = cardRef.current?.querySelector('svg.qr-code');
      if (!svg) { toast.error(t('Erreur de generation', 'Generation error')); return; }
      toast.success(t('Carte prete ! Faites une capture d\'ecran.', 'Card ready! Take a screenshot.'));
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'LocalSite.Club Pass',
      text: t(
        `${user?.name} vous invite a rejoindre LocalSite.Club ! Code: ${user?.referral_code}`,
        `${user?.name} invites you to join LocalSite.Club! Code: ${user?.referral_code}`
      ),
      url: `${window.location.origin}/register?ref=${user?.referral_code}`,
    };
    try {
      if (navigator.share) { await navigator.share(shareData); }
      else {
        await navigator.clipboard.writeText(shareData.url);
        toast.success(t('Lien copie !', 'Link copied!'));
      }
    } catch { /* user cancelled */ }
  };

  const handleInvite = async () => {
    const inviteUrl = `${window.location.origin}/register?ref=${user?.referral_code}`;
    const inviteText = t(
      `${user?.name} vous invite a rejoindre LocalSite.Club ! Rejoignez le mouvement et decouvrez les tresors de votre quartier. Inscrivez-vous ici : ${inviteUrl}`,
      `${user?.name} invites you to join LocalSite.Club! Join the movement and discover the treasures of your neighborhood. Sign up here: ${inviteUrl}`
    );
    try {
      if (navigator.share) {
        await navigator.share({ title: "LocalSite.Club — Let's make friends!", text: inviteText, url: inviteUrl });
      } else {
        await navigator.clipboard.writeText(inviteText);
        toast.success(t('Message d\'invitation copie !', 'Invite message copied!'));
      }
    } catch { /* user cancelled */ }
  };

  return (
    <OrbitLayout>
      <div className="max-w-2xl mx-auto space-y-8" data-testid="localsite-pass-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
            <CreditCard className="inline-block w-7 h-7 mr-2 text-[var(--ls-terracotta)] -mt-1" />
            {t('Mon LocalSite Pass', 'My LocalSite Pass')}
          </h1>
          <p className="text-gray-300 mt-2">
            {t(
              'Votre carte membre virtuelle pour profiter des rabais chez nos partenaires',
              'Your virtual member card to enjoy discounts at our partner businesses'
            )}
          </p>
        </div>

        {/* The Card */}
        <div ref={cardRef} className="relative mx-auto" style={{ maxWidth: '420px' }}>
          <div className={`relative bg-gradient-to-br from-[var(--ls-bark)] via-[#2A1F18] to-[var(--ls-bark)] rounded-3xl overflow-hidden shadow-2xl`}>
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-[var(--ls-terracotta)]/10 rounded-full -translate-y-16 translate-x-16" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[var(--ls-gold)]/10 rounded-full translate-y-12 -translate-x-12" />
            <div className="absolute top-1/2 right-0 w-2 h-16 bg-gradient-to-b from-transparent via-[var(--ls-terracotta)]/30 to-transparent rounded-l" />

            {/* Card Content */}
            <div className="relative p-6 sm:p-8">
              {/* Top row — Brand + Rank */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-[var(--ls-terracotta)]" />
                  <div>
                    <h2 className="font-heading text-lg font-bold text-white leading-tight">
                      Local<span className="text-[var(--ls-terracotta)]">Site</span>.Club
                    </h2>
                    <p className="text-white/30 text-[10px] uppercase tracking-[0.15em]">
                      {t('Carte Membre', 'Member Card')}
                    </p>
                  </div>
                </div>
                <div className={`px-3 py-1.5 rounded-full bg-gradient-to-r ${rank.gradient} text-white text-xs font-bold shadow-lg`}
                  data-testid="pass-rank-badge">
                  {rank.label}
                </div>
              </div>

              {/* Center — QR Code */}
              <div className="flex justify-center mb-6">
                <div className="bg-white rounded-2xl p-4 shadow-inner">
                  <QRCodeSVG
                    value={qrData}
                    size={160}
                    level="M"
                    className="qr-code"
                    fgColor="#1E1510"
                    bgColor="#FFFFFF"
                    data-testid="pass-qr-code"
                  />
                </div>
              </div>

              {/* Member Info */}
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-white font-heading" data-testid="pass-member-name">
                  {user?.name || 'Membre'}
                </h3>
                <p className="text-[var(--ls-terracotta)] text-sm font-mono font-medium tracking-wider mt-1" data-testid="pass-member-code">
                  {user?.referral_code || 'XXXXXXXX'}
                </p>
              </div>

              {/* Bottom row — Info */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <p className="text-white/30 text-[10px] uppercase tracking-wider">{t('Membre depuis', 'Member since')}</p>
                  <p className="text-white/70 text-xs font-medium">{memberSince}</p>
                </div>
                <div className="text-right">
                  <p className="text-white/30 text-[10px] uppercase tracking-wider">{t('Rabais', 'Discount')}</p>
                  <p className="text-[var(--ls-gold)] text-lg font-bold">10%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Card shadow effect */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[85%] h-6 bg-[var(--ls-bark)]/20 rounded-full blur-xl" />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 flex-wrap">
          <button onClick={handleDownload}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--ls-terracotta)] text-white rounded-full font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors shadow-lg shadow-[var(--ls-terracotta)]/20"
            data-testid="pass-download-btn">
            <Download className="w-4 h-4" />
            {t('Telecharger', 'Download')}
          </button>
          <button onClick={handleShare}
            className="flex items-center gap-2 px-6 py-3 bg-white border border-[hsl(40,20%,88%)] text-[var(--ls-text)] rounded-full font-semibold hover:bg-[var(--ls-sand-dark)] transition-colors"
            data-testid="pass-share-btn">
            <Share2 className="w-4 h-4" />
            {t('Partager', 'Share')}
          </button>
          <button onClick={handleInvite}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--ls-forest)] text-white rounded-full font-semibold hover:bg-[var(--ls-forest-light)] transition-colors shadow-lg shadow-[var(--ls-forest)]/20"
            data-testid="pass-invite-btn">
            <UserPlus className="w-4 h-4" />
            {t('Inviter un ami', 'Invite a friend')}
          </button>
        </div>

        {/* Let's Make Friends Banner */}
        <div className="bg-gradient-to-r from-[var(--ls-terracotta)]/5 via-[var(--ls-gold)]/5 to-[var(--ls-forest)]/5 border border-[var(--ls-terracotta)]/15 rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6" data-testid="lets-make-friends-banner">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/987d762f-6280-4d2f-beee-1ba5be49cef1/images/682fbd88cda1d28df49e687db44b53514e1a06f8c5c5a5f250550d301949655a.png"
            alt="Let's make friends"
            className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover flex-shrink-0"
          />
          <div className="text-center sm:text-left">
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-[var(--ls-text)]">
              {t("Faisons-nous des amis !", "Let's make friends!")}
            </h3>
            <p className="text-[var(--ls-text-muted)] mt-2 text-sm sm:text-base">
              {t(
                "Partagez votre code de parrainage avec vos amis et votre famille. Ensemble, soutenons nos commercants locaux et batissons une communaute forte !",
                "Share your referral code with friends and family. Together, let's support our local businesses and build a strong community!"
              )}
            </p>
            <button onClick={handleInvite}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--ls-terracotta)] text-white rounded-full font-semibold text-sm hover:bg-[var(--ls-terracotta-dark)] transition-colors"
              data-testid="pass-invite-banner-btn">
              <UserPlus className="w-4 h-4" />
              {t('Inviter maintenant', 'Invite now')}
            </button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[var(--ls-terracotta)]/10 rounded-xl flex items-center justify-center">
                <Star className="w-5 h-5 text-[var(--ls-terracotta)]" />
              </div>
              <h3 className="font-heading font-bold text-[var(--ls-text)]">{t('Comment utiliser', 'How to use')}</h3>
            </div>
            <ul className="space-y-2 text-sm text-[var(--ls-text-muted)]">
              <li className="flex items-start gap-2">
                <span className="text-[var(--ls-terracotta)] font-bold mt-0.5">1.</span>
                {t('Presentez votre code QR chez un partenaire', 'Show your QR code at a partner business')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[var(--ls-terracotta)] font-bold mt-0.5">2.</span>
                {t('Le commercant scanne votre carte', 'The merchant scans your card')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[var(--ls-terracotta)] font-bold mt-0.5">3.</span>
                {t('Profitez de 10% de rabais instantane', 'Enjoy an instant 10% discount')}
              </li>
            </ul>
          </div>

          <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[var(--ls-gold)]/10 rounded-xl flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[var(--ls-gold)]" />
              </div>
              <h3 className="font-heading font-bold text-[var(--ls-text)]">{t('Statistiques', 'Statistics')}</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--ls-text-muted)]">{t('Rang', 'Rank')}</span>
                <span className="text-sm font-semibold" style={{ color: rank.color }}>{rank.label}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--ls-text-muted)]">{t('Code de parrainage', 'Referral code')}</span>
                <span className="text-sm font-mono font-semibold text-[var(--ls-text)]">{user?.referral_code || '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--ls-text-muted)]">{t('Rabais disponible', 'Available discount')}</span>
                <span className="text-sm font-bold text-[var(--ls-terracotta)]">10%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OrbitLayout>
  );
};

export default LocalSitePassPage;
